import { useCallback } from "react";
import classnames from "classnames";

import Switch from "@/features/shared/components/switch/Switch";
import panelCommonStyles from "@/features/shared/widgets/map/controls/panel-common.module.css";
import {
  ControlItem,
  LayerToggleLabel,
  LayerToggleRow,
  LayersPanelContainer,
} from "@/features/shared/widgets/map/map.styles";
import { useTranslation } from "@/lib/i18n";

import type { ModelMapStaticOverlayEntry } from "./use-model-map-static-overlays";
import styles from "./model-map-static-overlays-panel.module.css";

export type StaticOverlayUiState = {
  visible: boolean;
};

export type StaticOverlayUiById = Record<string, StaticOverlayUiState>;

type ModelMapStaticOverlaysPanelProps = {
  entries: ModelMapStaticOverlayEntry[];
  uiById: StaticOverlayUiById;
  onSetVisible: (instanceId: string, visible: boolean) => void;
};

/*
 * TODO(unify-map-control-layout): Model-map stacks the gridded control and static toggles with local
 * Stack/gap. If map widget gains a shared vertical panel primitive for its config tab, consider
 * reusing or aligning spacing here so both widgets stay visually consistent—without coupling branches.
 */

export function ModelMapStaticOverlaysPanel({
  entries,
  uiById,
  onSetVisible,
}: ModelMapStaticOverlaysPanelProps) {
  if (entries.length === 0) return null;

  return (
    <div className={styles.panelShell}>
      <LayersPanelContainer
        className={classnames(panelCommonStyles.frostedPanel)}
        style={{ width: "100%", minWidth: 0, boxSizing: "border-box" }}
      >
        {entries.map((entry) => (
          <StaticOverlayRow
            key={entry.instanceId}
            entry={entry}
            visible={uiById[entry.instanceId]?.visible ?? entry.enabledByDefault}
            onSetVisible={onSetVisible}
          />
        ))}
      </LayersPanelContainer>
    </div>
  );
}

function StaticOverlayRow({
  entry,
  visible,
  onSetVisible,
}: {
  entry: ModelMapStaticOverlayEntry;
  visible: boolean;
  onSetVisible: (instanceId: string, visible: boolean) => void;
}) {
  const { t } = useTranslation();
  const toggle = useCallback(() => {
    onSetVisible(entry.instanceId, !visible);
  }, [entry.instanceId, onSetVisible, visible]);

  return (
    <ControlItem
      className={classnames(panelCommonStyles.rowDivider)}
      isEnabled
      isReady
      isActive
      isForecast={false}
    >
      <LayerToggleRow>
        <LayerToggleLabel onClick={toggle}>{entry.label}</LayerToggleLabel>
        <Switch
          checked={visible}
          onClick={toggle}
          inputProps={{
            "aria-label": t("map.model_map_static_overlay_toggle_aria", {
              name: entry.label,
            }),
          }}
        />
      </LayerToggleRow>
    </ControlItem>
  );
}

/* Skipped for v1 (follow-up): disable toggles while layer is loading; persist visibility across
 * ModelMapLayersBody key remount (lift state); reset row keys on CMS reorder strategy. */
